/**
 * One diverging row from a per-value count distribution.
 *
 * Lifted from Quick Study (`apps/frontend/src/components/quick-study-results/divergingStacked.ts`).
 * Quick Study is not migrated onto this module; a frontend parity test is the drift guard.
 */
import { roundHalfAwayFromZero, roundHalfAwayFromZeroAt } from '../../../foundations/producer-rounding'
import { divergingBands, DivergingBands } from './divergingBands'

export interface DivergingRow {
  n: number
  low: number
  neutral: number
  high: number
  lowPct: number
  neutralPct: number
  highPct: number
  mean: number
  /** Top-2-box % (v >= scaleMax − 1), the hero stat, independent of the band
   *  split. A top-two statistic: neither favorable share nor Python's
   *  `top_box_share`, which is the top authored point only (contrast in
   *  statistic-definitions.v1.json). */
  top2Pct: number
  /** Bottom-2-box % (v <= scaleMin + 1): the top-two box of a reversed scale,
   *  independent of the band split (the low band of an even scale is a half). */
  bottom2Pct: number
}

/** Build a diverging row from a per-value count distribution (the shape
 *  monadic_rating[].distribution ships). Returns null when no usable counts.
 *  `declaredBands` are the canonical policy bands of a declared rating unit
 *  (`ratingPolicyBands`); without them the visual range split applies. */
export function divergingRowFromDistribution(
  distribution: Record<number | string, number> | undefined | null,
  scaleMin: number,
  scaleMax: number,
  declaredBands?: DivergingBands | null,
): DivergingRow | null {
  const bands = declaredBands ?? divergingBands(scaleMin, scaleMax)
  if (!bands) return null
  let n = 0
  let low = 0
  let neutral = 0
  let high = 0
  let top2 = 0
  let bottom2 = 0
  let sum = 0
  for (let v = scaleMin; v <= scaleMax; v++) {
    const c = Number((distribution ?? {})[v] ?? 0) || 0
    if (c <= 0) continue
    n += c
    sum += v * c
    if (v >= bands.highMin) high += c
    else if (v <= bands.lowMax) low += c
    else neutral += c
    if (v >= scaleMax - 1) top2 += c
    if (v <= scaleMin + 1) bottom2 += c
  }
  if (!n) return null
  // The producer's rounding rule (half away from zero), so a .5 share agrees with Python.
  const pct = (x: number): number => roundHalfAwayFromZero((100 * x) / n)
  return {
    n,
    low,
    neutral,
    high,
    lowPct: pct(low),
    neutralPct: pct(neutral),
    highPct: pct(high),
    mean: roundHalfAwayFromZeroAt(sum / n, 2),
    top2Pct: pct(top2),
    bottom2Pct: pct(bottom2),
  }
}
